import React from 'react';
import { makeStyles } from '@mui/styles';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Box,
} from '@mui/material';
import { Launch, GitHub, ArrowForward } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { getAllProjects } from '../utils/projects';
import { getPostsByTag } from '../utils/blog';

const useStyles = makeStyles((theme) => ({
  pageHeader: {
    textAlign: 'center',
    marginBottom: theme.spacing(2),
    fontWeight: 700,
  },
  pageSubtitle: {
    textAlign: 'center',
    color: theme.palette.text.secondary,
    maxWidth: '600px',
    margin: `0 auto ${theme.spacing(6)}`,
  },
  projectCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
    '&:hover': {
      transform: 'translateY(-8px)',
      boxShadow: theme.shadows[12],
    },
  },
  projectTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
  },
  projectDescription: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
    lineHeight: 1.6,
    flexGrow: 1,
  },
  cardActions: {
    padding: theme.spacing(2),
    paddingTop: 0,
    justifyContent: 'space-between',
  },
  actionButton: {
    fontWeight: 600,
  },
  relatedPosts: {
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  relatedPostLink: {
    color: theme.palette.secondary.main,
    textDecoration: 'none',
    fontSize: '0.875rem',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
}));

const ProjectsPage = () => {
  const classes = useStyles();
  const projects = getAllProjects();

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 6 }}>
        <Typography variant="h2" className={classes.pageHeader}>
          Projects
        </Typography>

        <Typography variant="h6" className={classes.pageSubtitle}>
          A collection of projects I've built, each showcasing different
          technologies and problem-solving approaches.
        </Typography>

        <Grid container spacing={4}>
          {projects.map((project, index) => (
            <Grid size={{ xs: 12, md: 4 }} key={index}>
              <Card className={classes.projectCard}>
                <CardContent>
                  <Typography variant="h5" className={classes.projectTitle}>
                    {project.title}
                  </Typography>

                  <Typography
                    variant="body2"
                    className={classes.projectDescription}
                  >
                    {project.summary}
                  </Typography>

                  {(() => {
                    const related = getPostsByTag(project.tag);
                    if (related.length === 0) return null;
                    return (
                      <Box className={classes.relatedPosts}>
                        <Typography
                          variant="subtitle2"
                          style={{ fontWeight: 600, marginBottom: 8 }}
                        >
                          Related Posts:
                        </Typography>
                        {related.map((post) => (
                          <Box key={post.slug} sx={{ mb: 0.5 }}>
                            <Link
                              to={`/blog/${post.slug}`}
                              className={classes.relatedPostLink}
                            >
                              {post.title}
                            </Link>
                          </Box>
                        ))}
                      </Box>
                    );
                  })()}
                </CardContent>

                <CardActions className={classes.cardActions}>
                  <Box>
                    {project.codeLink && project.codeLink !== '#' && (
                      <Button
                        size="small"
                        color="primary"
                        href={project.codeLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        startIcon={<GitHub />}
                        className={classes.actionButton}
                      >
                        Code
                      </Button>
                    )}
                    <Button
                      size="small"
                      color="primary"
                      href={project.demoLink}
                      target={project.demoLink === '#' ? '_self' : '_blank'}
                      rel="noopener noreferrer"
                      startIcon={<Launch />}
                      className={classes.actionButton}
                      disabled={project.demoLink === '#'}
                    >
                      Live Site
                    </Button>
                  </Box>
                  <Button
                    size="small"
                    color="primary"
                    component={Link}
                    to={`/projects/${project.slug}`}
                    endIcon={<ArrowForward />}
                    className={classes.actionButton}
                  >
                    Learn More
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Container>
  );
};

export default ProjectsPage;
